import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { PaperPlaneRight, Image, X, Circle } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws");

// Notification sound
const playNotifSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
};

export function ChatPanel() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [connected, setConnected] = useState(false);
  const [chatUsers, setChatUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null); // null = hidden, string = filtering
  const [mentionIdx, setMentionIdx] = useState(0);
  const [pendingMentions, setPendingMentions] = useState([]);
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [showReactions, setShowReactions] = useState(null);
  const messagesEndRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const REACTIONS = ["👍", "❤️", "😂", "👀", "🔥", "✅"];

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Load messages + users for mentions
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/chat/messages?limit=100`, { headers }),
      axios.get(`${API}/chat/users`, { headers }),
    ]).then(([mRes, uRes]) => {
      setMessages(mRes.data || []);
      setChatUsers(uRes.data || []);
      setTimeout(scrollToBottom, 100);
    }).catch(() => {}).finally(() => setLoading(false));
    axios.post(`${API}/chat/mark-read`, {}, { headers }).catch(() => {});
  }, [token]);

  // WebSocket connection
  useEffect(() => {
    if (!token) return;
    let alive = true;

    const connect = () => {
      const ws = new WebSocket(`${WS_URL}/api/ws/chat?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        if (alive) setConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "new_message") {
            setMessages(prev => [...prev, data.message]);
            setTimeout(scrollToBottom, 50);
            if (data.message.user_id !== user?.id) {
              playNotifSound();
            }
            axios.post(`${API}/chat/mark-read`, {}, { headers }).catch(() => {});
          } else if (data.type === "reaction_update") {
            setMessages(prev => prev.map(m => m.id === data.message_id ? { ...m, reactions: data.reactions } : m));
          } else if (data.type === "user_joined" || data.type === "user_left") {
            setOnlineUsers(data.online || []);
          }
        } catch {}
      };

      ws.onclose = () => {
        if (alive) {
          setConnected(false);
          reconnectRef.current = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => {};
    };

    connect();

    // Ping keep-alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);

    return () => {
      alive = false;
      clearInterval(pingInterval);
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [token, user?.id]);

  const handleSend = async () => {
    const msg = text.trim();
    if (!msg) return;
    const mentions = [...pendingMentions];
    setText("");
    setPendingMentions([]);
    setSending(true);
    try {
      await axios.post(`${API}/chat/messages`, { text: msg, mentions }, { headers });
    } catch { toast.error("Error al enviar"); setText(msg); }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (mentionQuery !== null) {
      const filtered = chatUsers.filter(u => u.id !== user?.id && u.name.toLowerCase().includes((mentionQuery || "").toLowerCase()));
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx(i => Math.min(i + 1, filtered.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx(i => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (filtered[mentionIdx]) insertMention(filtered[mentionIdx]);
        return;
      }
      if (e.key === "Escape") { setMentionQuery(null); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    // Detect @mention
    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const match = before.match(/@(\w*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionIdx(0);
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (u) => {
    const cursor = inputRef.current?.selectionStart || text.length;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const match = before.match(/@(\w*)$/);
    if (match) {
      const newBefore = before.slice(0, match.index) + `@${u.name} `;
      setText(newBefore + after);
      setPendingMentions(prev => [...prev.filter(m => m.id !== u.id), { id: u.id, name: u.name }]);
      setMentionQuery(null);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = inputRef.current.selectionEnd = newBefore.length;
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  const handleReaction = async (msgId, emoji) => {
    setShowReactions(null);
    try {
      await axios.post(`${API}/chat/messages/${msgId}/react`, { emoji }, { headers });
    } catch { toast.error("Error"); }
  };

  // Render text with highlighted mentions
  const renderMessageText = (msg) => {
    const txt = msg.text || "";
    const mentions = msg.mentions || [];
    if (mentions.length === 0) return <span>{txt}</span>;
    const mentionNames = mentions.map(m => m.name);
    const regex = new RegExp(`(@(?:${mentionNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join("|")}))`, "g");
    const parts = txt.split(regex);
    return parts.map((part, i) => {
      if (mentionNames.some(n => part === `@${n}`)) {
        return <span key={i} className="font-bold text-blue-300 bg-blue-500/20 px-0.5 rounded">{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  const renderMessageTextOther = (msg) => {
    const txt = msg.text || "";
    const mentions = msg.mentions || [];
    if (mentions.length === 0) return <span>{txt}</span>;
    const mentionNames = mentions.map(m => m.name);
    const regex = new RegExp(`(@(?:${mentionNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join("|")}))`, "g");
    const parts = txt.split(regex);
    return parts.map((part, i) => {
      if (mentionNames.some(n => part === `@${n}`)) {
        return <span key={i} className="font-bold text-blue-600 bg-blue-100 px-0.5 rounded">{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await axios.post(`${API}/chat/messages/image`, form, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
    } catch { toast.error("Error al subir imagen"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const getInitials = (name) => (name || "?").slice(0, 2).toUpperCase();
  const getColor = (name) => {
    const colors = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#F97316"];
    let hash = 0;
    for (let i = 0; i < (name || "").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col" data-testid="chat-panel">
      {/* Header */}
      <div className="shrink-0 bg-white border-b px-5 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Canal General</h1>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Circle size={8} weight="fill" className={connected ? "text-green-500" : "text-slate-300"} />
            <span className="text-[10px] text-slate-500">{onlineUsers.length} en linea</span>
            {onlineUsers.length > 0 && (
              <span className="text-[10px] text-slate-400 ml-1">
                {onlineUsers.slice(0, 3).map(u => u.name?.split(" ")[0]).join(", ")}
                {onlineUsers.length > 3 && ` +${onlineUsers.length - 3}`}
              </span>
            )}
          </div>
        </div>
        <div className="flex -space-x-1.5">
          {onlineUsers.slice(0, 6).map(u => (
            <div key={u.id} className="w-7 h-7 rounded-full text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white"
              style={{ backgroundColor: getColor(u.name) }} title={u.name}>
              {getInitials(u.name)}
            </div>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-20">
            <p className="text-sm text-slate-400">Sin mensajes aun. Se el primero en escribir!</p>
          </div>
        )}
        {messages.map((msg, idx) => {
          const isOwn = msg.user_id === user?.id;
          const showAvatar = idx === 0 || messages[idx - 1]?.user_id !== msg.user_id;
          const time = msg.created_at ? (() => { try { return format(new Date(msg.created_at), "HH:mm", { locale: es }); } catch { return ""; }})() : "";
          const date = msg.created_at ? (() => { try { return format(new Date(msg.created_at), "dd MMM yyyy", { locale: es }); } catch { return ""; }})() : "";
          const prevDate = idx > 0 && messages[idx - 1]?.created_at ? (() => { try { return format(new Date(messages[idx - 1].created_at), "dd MMM yyyy", { locale: es }); } catch { return ""; }})() : "";
          const showDate = date && date !== prevDate;

          return (
            <div key={msg.id || idx}>
              {showDate && (
                <div className="flex items-center justify-center my-3">
                  <span className="text-[10px] font-semibold text-slate-400 bg-white px-3 py-1 rounded-full border">{date}</span>
                </div>
              )}
              <div className={`flex gap-2.5 ${isOwn ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${msg.id}`}
                onMouseEnter={() => setHoveredMsg(msg.id)} onMouseLeave={() => { if (showReactions !== msg.id) setHoveredMsg(null); }}>
                {showAvatar ? (
                  <div className="w-8 h-8 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0"
                    style={{ backgroundColor: getColor(msg.user_name) }}>
                    {getInitials(msg.user_name)}
                  </div>
                ) : <div className="w-8 shrink-0" />}
                <div className={`max-w-[70%] ${isOwn ? "items-end" : "items-start"} relative`}>
                  {showAvatar && (
                    <p className={`text-[10px] font-semibold mb-0.5 ${isOwn ? "text-right text-blue-600" : "text-slate-500"}`}>{msg.user_name}</p>
                  )}
                  <div className="relative group/msg">
                    <div className={`rounded-2xl px-3.5 py-2 ${isOwn ? "bg-blue-600 text-white rounded-tr-sm" : "bg-white border text-slate-800 rounded-tl-sm"} shadow-sm`}>
                      {msg.type === "image" && msg.image_url && (
                        <a href={`${BACKEND_URL}${msg.image_url}`} target="_blank" rel="noopener noreferrer">
                          <img src={`${BACKEND_URL}${msg.image_url}`} alt={msg.text} className="max-w-[250px] max-h-[200px] rounded-lg mb-1 cursor-pointer hover:opacity-90" />
                        </a>
                      )}
                      {msg.type !== "image" && msg.text && (
                        <p className="text-sm whitespace-pre-wrap break-words">{isOwn ? renderMessageText(msg) : renderMessageTextOther(msg)}</p>
                      )}
                    </div>
                    {/* Reaction button */}
                    {hoveredMsg === msg.id && (
                      <button onClick={() => setShowReactions(showReactions === msg.id ? null : msg.id)}
                        className={`absolute ${isOwn ? "-left-7" : "-right-7"} top-0 w-6 h-6 rounded-full bg-white border shadow-sm flex items-center justify-center text-slate-400 hover:text-amber-500 hover:scale-110 transition-all`}
                        data-testid={`react-btn-${msg.id}`}>
                        <span className="text-xs">😊</span>
                      </button>
                    )}
                    {/* Reaction picker */}
                    {showReactions === msg.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowReactions(null)} />
                        <div className={`absolute ${isOwn ? "right-0" : "left-0"} -top-10 z-20 bg-white border rounded-full shadow-lg px-1.5 py-1 flex gap-0.5`} data-testid={`react-picker-${msg.id}`}>
                          {REACTIONS.map(emoji => (
                            <button key={emoji} onClick={() => handleReaction(msg.id, emoji)}
                              className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center text-base hover:scale-125 transition-transform">
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Reactions display */}
                  {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                    <div className={`flex flex-wrap gap-1 mt-0.5 ${isOwn ? "justify-end" : ""}`}>
                      {Object.entries(msg.reactions).map(([emoji, userIds]) => (
                        <button key={emoji} onClick={() => handleReaction(msg.id, emoji)}
                          className={`flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded-full border transition-colors ${(userIds || []).includes(user?.id) ? "bg-blue-50 border-blue-300" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                          data-testid={`reaction-${msg.id}-${emoji}`}>
                          {emoji} <span className="text-[9px] font-semibold text-slate-600">{(userIds || []).length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className={`text-[9px] mt-0.5 ${isOwn ? "text-right" : ""} text-slate-400`}>{time}</p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="shrink-0 bg-white border-t px-4 py-3" data-testid="chat-input-area">
        {/* Mention dropdown */}
        {mentionQuery !== null && (() => {
          const filtered = chatUsers.filter(u => u.id !== user?.id && u.name.toLowerCase().includes((mentionQuery || "").toLowerCase())).slice(0, 8);
          return filtered.length > 0 ? (
            <div className="mb-2 bg-white border rounded-xl shadow-lg max-h-40 overflow-y-auto" data-testid="mention-dropdown">
              {filtered.map((u, i) => (
                <button key={u.id} onClick={() => insertMention(u)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-blue-50 transition-colors ${i === mentionIdx ? "bg-blue-50" : ""}`}>
                  <div className="w-6 h-6 rounded-full text-white text-[8px] font-bold flex items-center justify-center shrink-0"
                    style={{ backgroundColor: getColor(u.name) }}>
                    {getInitials(u.name)}
                  </div>
                  <span className="font-semibold text-slate-800">{u.name}</span>
                  <span className="text-[10px] text-slate-400 ml-auto">{u.role}</span>
                </button>
              ))}
            </div>
          ) : null;
        })()}
        <div className="flex items-end gap-2">
          <button onClick={() => fileRef.current?.click()}
            className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors shrink-0" title="Enviar imagen" data-testid="chat-image-btn">
            <Image size={20} />
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              placeholder="Escribe un mensaje... (@mencionar)"
              rows={1}
              className="w-full resize-none rounded-xl border-2 border-slate-200 focus:border-blue-400 focus:outline-none px-4 py-2.5 text-sm max-h-24 overflow-y-auto"
              style={{ minHeight: "42px" }}
              data-testid="chat-input"
            />
          </div>
          <button onClick={handleSend} disabled={sending || !text.trim()}
            className="p-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0" data-testid="chat-send-btn">
            <PaperPlaneRight size={18} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}
